import { useContext, useEffect, useReducer, useState } from "react"
import {
	Accordion, AccordionDetails, AccordionSummary,
	Alert, Box, Button, Chip, CircularProgress, FormControl,
	IconButton, InputLabel, MenuItem, Paper, Select, Stack,
	TextField, Tooltip, Typography
} from "@mui/material"
import { useQueryClient } from "react-query"

import ExpandMoreIcon			from '@mui/icons-material/ExpandMore'
import AddCircleOutlineIcon		from '@mui/icons-material/AddCircleOutline'
import DeleteIcon				from '@mui/icons-material/DeleteOutline'
import EditIcon					from '@mui/icons-material/EditOutlined'
import CheckIcon				from '@mui/icons-material/Check'
import CloseIcon				from '@mui/icons-material/Close'

import TransferList		from "../../../components/TransferList"
import PortfolioContext	from "../context"
import Loading			from "../../../components/Loading"
import config			from "../../../common/Config"
import useInternalFetch	from "../../../api/apiInternal"

const settingsReducer = (state, action) => {
	switch (action.type) {
		case 'load':   return { ...action.payload }
		case 'update': return { ...state, [action.field]: action.payload }
		default:       return state
	}
}

const initialSettings = {
	convertToDollar			: false,
	analyzeQuarterly		: false,
	allowFailedRiskGrowth	: false,
	investmentIntervalDays	: 0,
	investByDate			: false,
}

const TabParameters = () => {

	const internalFetch = useInternalFetch()

	const {
		userViews, setUserViews, allFields,
		convertToDollar, setConvertToDollar,
		analyzeQuarterly, setAnalyzeQuarterly,
		allowFailedRiskGrowth, setAllowFailedRiskGrowth,
		investmentIntervalDays, setInvestmentIntervalDays,
		investByDate, setInvestByDate,
	} = useContext(PortfolioContext)

	const [settings, dispatch]				= useReducer(settingsReducer, initialSettings)
	const [views, setViews]					= useState([])
	const [viewsLoaded, setViewsLoaded]		= useState(false)
	const [expandedIndex, setExpandedIndex]	= useState(null)
	const [saveStatus, setSaveStatus]		= useState(null)
	const [isSaving, setIsSaving]			= useState(false)
	const [editingIndex, setEditingIndex]	= useState(null)
	const [editingName, setEditingName]		= useState('')
	const [fieldErrors, setFieldErrors]		= useState({})

	const queryClient = useQueryClient()

	const fields = allFields
		? [...allFields].sort((a, b) =>
			a.category === b.category
				? a.label > b.label ? 1 : -1
				: a.category > b.category ? 1 : -1
		)
		: null

	useEffect(() => {
		if (!userViews) return
		const mapped = userViews.map(v => ({
			id		: v.id,
			name	: v.name,
			fields	: (v.portfolioViewFields || [])
				.sort((a, b) => a.fieldOrder - b.fieldOrder)
				.map(f => f.fieldId)
		}))
		setViews(mapped)
		setViewsLoaded(true)
		if (mapped.length > 0) setExpandedIndex(0)
	}, [userViews])

	useEffect(() => {
		dispatch({ type: 'load', payload: {
			convertToDollar			: convertToDollar || false,
			analyzeQuarterly		: analyzeQuarterly || false,
			allowFailedRiskGrowth	: allowFailedRiskGrowth || false,
			investmentIntervalDays	: investmentIntervalDays || 0,
			investByDate			: investByDate || false,
		}})
	}, [convertToDollar, analyzeQuarterly, allowFailedRiskGrowth, investmentIntervalDays, investByDate])

	const handleAddView = () => {
		const newView = { name: `Nova view ${views.length + 1}`, fields: [] }
		const updated = [...views, newView]
		setViews(updated)
		const newIndex = updated.length - 1
		setExpandedIndex(newIndex)
		setEditingIndex(newIndex)
		setEditingName(newView.name)
	}

	const handleDeleteView = (e, index) => {
		e.stopPropagation()
		const updated = views.filter((_, i) => i !== index)
		setViews(updated)
		setFieldErrors(prev => {
			const next = { ...prev }
			delete next[index]
			return next
		})
		if (expandedIndex === index) setExpandedIndex(updated.length > 0 ? 0 : null)
		else if (expandedIndex > index) setExpandedIndex(expandedIndex - 1)
	}

	const handleStartEdit = (e, index) => {
		e.stopPropagation()
		setEditingIndex(index)
		setEditingName(views[index].name)
	}

	const handleConfirmEdit = (e, index) => {
		e.stopPropagation()
		if (!editingName.trim()) return
		setViews(prev => prev.map((v, i) => i === index ? { ...v, name: editingName.trim() } : v))
		setEditingIndex(null)
	}

	const handleCancelEdit = (e) => {
		e.stopPropagation()
		setEditingIndex(null)
	}

	const handleFieldsChange = (index, newFields) => {
		setViews(prev => prev.map((v, i) => i === index ? { ...v, fields: newFields } : v))
		if (newFields.length > 0) {
			setFieldErrors(prev => { const n = { ...prev }; delete n[index]; return n })
		}
	}

	const handleAccordionChange = (index) => {
		setExpandedIndex(prev => prev === index ? null : index)
	}

	const handleSave = async () => {
		if (!viewsLoaded) {
			setSaveStatus({ severity: 'error', message: 'Aguardando carregamento das views. Tente novamente.' })
			return
		}

		const errors = {}
		views.forEach((v, i) => {
			if (v.fields.length === 0) errors[i] = true
		})
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors)
			setExpandedIndex(Number(Object.keys(errors)[0]))
			setSaveStatus({ severity: 'error', message: 'Cada view precisa ter ao menos uma coluna selecionada.' })
			return
		}

		const options = {
			method	: 'POST',
			headers	: { 'content-type': 'application/json; charset=utf-8' },
			body	: JSON.stringify({
				views	: views.map(v => ({ name: v.name, fields: v.fields })),
				settings: {
					...settings,
					preventConsecutiveInvestment: settings.investmentIntervalDays > 0,
				}
			})
		}

		try {
			setIsSaving(true)
			const results = await internalFetch(config.apiUrl + '/api/portfolio/settings', options)
			const payload = results.data

			if (results.status === 200) {
				setUserViews(Array.isArray(payload?.views) ? payload.views : [])
				setConvertToDollar(payload?.settings?.convertToDollar ?? settings.convertToDollar)
				setAnalyzeQuarterly(payload?.settings?.analyzeQuarterly ?? settings.analyzeQuarterly)
				setAllowFailedRiskGrowth(payload?.settings?.allowFailedRiskGrowth ?? settings.allowFailedRiskGrowth)
				setInvestmentIntervalDays(payload?.settings?.investmentIntervalDays ?? settings.investmentIntervalDays)
				setInvestByDate(payload?.settings?.investByDate ?? settings.investByDate)
				setSaveStatus({ severity: 'success', message: 'Configurações salvas com sucesso.' })
				queryClient.invalidateQueries(['portfolio_ACAO'])
				queryClient.invalidateQueries(['portfolio_STOCK'])
				queryClient.invalidateQueries(['portfolioGuidance_ACAO'])
				queryClient.invalidateQueries(['portfolioGuidance_STOCK'])
			} else {
				setSaveStatus({ severity: 'error', message: 'Erro ao salvar configurações.' })
			}
		} catch {
			setSaveStatus({ severity: 'error', message: 'Erro ao salvar configurações.' })
		} finally {
			setIsSaving(false)
		}
	}

	if (!fields) return <Loading />

	return (
		<Box>
			{saveStatus && (
				<Alert severity={saveStatus.severity} onClose={() => setSaveStatus(null)} sx={{ mt: 1, width: '97%' }}>
					{saveStatus.message}
				</Alert>
			)}
			<Box padding={2} display="flex" justifyContent="center" flexDirection="column" alignItems="center">
				<Stack spacing={2} sx={{ width: '100%', maxWidth: (theme) => theme.breakpoints.values.md }}>

					{/* Seção de views */}
					<Paper>
						<Stack padding={2} spacing={1}>
							<Box display="flex" justifyContent="space-between" alignItems="center">
								<Typography variant="h6">Minhas views</Typography>
								<Tooltip title="Nova view">
									<IconButton size="small" onClick={handleAddView} color="primary">
										<AddCircleOutlineIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Box>

							{views.length === 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ pb: 1 }}>
									Nenhuma view criada ainda. Clique em + para adicionar.
								</Typography>
							)}

							{views.map((view, index) => (
								<Accordion
									key={index}
									expanded={expandedIndex === index}
									onChange={() => handleAccordionChange(index)}
									disableGutters
									elevation={0}
									sx={{
										border		: '1px solid',
										borderColor	: fieldErrors[index] ? 'error.main' : 'divider',
										borderRadius: '4px !important',
										'&:before'	: { display: 'none' },
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
										sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 } }}
									>
										{editingIndex === index ? (
											<>
												<TextField
													variant="standard"
													value={editingName}
													onChange={(e) => setEditingName(e.target.value)}
													onKeyDown={(e) => {
														if (e.key === 'Enter') handleConfirmEdit(e, index)
														if (e.key === 'Escape') handleCancelEdit(e)
													}}
													autoFocus
													onClick={(e) => e.stopPropagation()}
													sx={{
														flex: 1,
														'& input': { fontSize: '0.8rem', fontWeight: 500, padding: 0 },
														'& .MuiInput-root': { marginTop: 0 },
													}}
												/>
												<IconButton size="small" onClick={(e) => handleConfirmEdit(e, index)}>
													<CheckIcon sx={{ fontSize: 16 }} />
												</IconButton>
												<IconButton size="small" onClick={handleCancelEdit}>
													<CloseIcon sx={{ fontSize: 16 }} />
												</IconButton>
											</>
										) : (
											<>
												<Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
													{view.name}
												</Typography>
												<Chip
													label={`${view.fields.length} coluna${view.fields.length !== 1 ? 's' : ''}`}
													size="small"
													color={fieldErrors[index] ? 'error' : 'default'}
													sx={{ fontSize: '0.7rem', height: 20 }}
												/>
												<IconButton size="small" onClick={(e) => handleStartEdit(e, index)}>
													<EditIcon sx={{ fontSize: 16 }} />
												</IconButton>
												<IconButton size="small" onClick={(e) => handleDeleteView(e, index)}>
													<DeleteIcon sx={{ fontSize: 16, color: 'error.main' }} />
												</IconButton>
											</>
										)}
									</AccordionSummary>
									<AccordionDetails sx={{ pt: 0, pb: 2 }}>
										{fieldErrors[index] && (
											<Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
												Selecione ao menos uma coluna para esta view.
											</Alert>
										)}
										<Box display="flex" justifyContent="center">
											<TransferList
												options={fields}
												selectedOptions={view.fields}
												onChange={(newFields) => handleFieldsChange(index, newFields)}
											/>
										</Box>
									</AccordionDetails>
								</Accordion>
							))}
						</Stack>
					</Paper>

					{/* Configurações gerais */}
					<Paper>
						<Stack direction="column" spacing={2} padding={2}>
							<Typography variant="h6">Configurações Gerais</Typography>
							<Box display="flex" alignItems="center" gap={3}>
								<FormControl size="small" sx={{ minWidth: 240, flexShrink: 0 }}>
									<InputLabel>Converter para dólar</InputLabel>
									<Select
										label="Converter para dólar"
										value={settings.convertToDollar ? 'yes' : 'no'}
										onChange={(e) => dispatch({ type: 'update', field: 'convertToDollar', payload: e.target.value === 'yes' })}
									>
										<MenuItem value="no">Não</MenuItem>
										<MenuItem value="yes">Sim</MenuItem>
									</Select>
								</FormControl>
								<Typography variant="body2" color="text.secondary">
									Converte valores financeiros (receita, lucro, dívida, etc.) para dólar americano na visualização do portfólio. Útil para comparar ações BR e US numa mesma moeda.
								</Typography>
							</Box>
							<Box display="flex" alignItems="center" gap={3}>
								<FormControl size="small" sx={{ minWidth: 240, flexShrink: 0 }}>
									<InputLabel>Tipo de análise de resultados</InputLabel>
									<Select
										label="Tipo de análise de resultados"
										value={settings.analyzeQuarterly ? 'quarterly' : 'annual'}
										onChange={(e) => dispatch({ type: 'update', field: 'analyzeQuarterly', payload: e.target.value === 'quarterly' })}
									>
										<MenuItem value="annual">Anual</MenuItem>
										<MenuItem value="quarterly">Trimestral</MenuItem>
									</Select>
								</FormControl>
								<Typography variant="body2" color="text.secondary">
									Define o período usado na coluna "Resultado" do portfólio. No modo trimestral, exibe e avalia o último resultado trimestral divulgado; no anual, usa o resultado anual (FY).
								</Typography>
							</Box>
						</Stack>
					</Paper>

					{/* Configurações de Aporte */}
					<Paper>
						<Stack direction="column" spacing={2} padding={2}>
							<Typography variant="h6">Configurações de Aporte</Typography>
							<Box display="flex" alignItems="center" gap={3}>
								<FormControl size="small" sx={{ minWidth: 240, flexShrink: 0 }}>
									<InputLabel>Investir em empresas reprovadas</InputLabel>
									<Select
										label="Investir em empresas reprovadas"
										value={settings.allowFailedRiskGrowth ? 'yes' : 'no'}
										onChange={(e) => dispatch({ type: 'update', field: 'allowFailedRiskGrowth', payload: e.target.value === 'yes' })}
									>
										<MenuItem value="no">Não</MenuItem>
										<MenuItem value="yes">Sim</MenuItem>
									</Select>
								</FormControl>
								<Typography variant="body2" color="text.secondary">
									Quando "Não", ativos com análise de Risco x Crescimento Reprovada não são sugeridos para aporte.
								</Typography>
							</Box>
							<Box display="flex" alignItems="center" gap={3}>
								<FormControl size="small" sx={{ minWidth: 240, flexShrink: 0 }}>
									<InputLabel>Intervalo mínimo entre aportes</InputLabel>
									<Select
										label="Intervalo mínimo entre aportes"
										value={settings.investmentIntervalDays}
										onChange={(e) => dispatch({ type: 'update', field: 'investmentIntervalDays', payload: Number(e.target.value) })}
									>
										<MenuItem value={0}>Sem intervalo</MenuItem>
										<MenuItem value={90}>3 meses</MenuItem>
										<MenuItem value={180}>6 meses</MenuItem>
										<MenuItem value={360}>12 meses</MenuItem>
									</Select>
								</FormControl>
								<Typography variant="body2" color="text.secondary">
									Impede que o mesmo ativo seja sugerido para aporte antes do intervalo mínimo desde a última compra.
								</Typography>
							</Box>
							<Box display="flex" alignItems="center" gap={3}>
								<FormControl size="small" sx={{ minWidth: 240, flexShrink: 0 }}>
									<InputLabel>Critério de prioridade</InputLabel>
									<Select
										label="Critério de prioridade"
										value={settings.investByDate ? 'date' : 'gap'}
										onChange={(e) => dispatch({ type: 'update', field: 'investByDate', payload: e.target.value === 'date' })}
									>
										<MenuItem value="gap">Gap do alvo</MenuItem>
										<MenuItem value="date">Sem aporte há mais tempo</MenuItem>
									</Select>
								</FormControl>
								<Typography variant="body2" color="text.secondary">
									Define como o próximo ativo é escolhido. "Gap do alvo" prioriza o mais distante da alocação desejada; "Sem aporte há mais tempo" prioriza o que está há mais dias sem compra (ativos nunca aportados ficam no topo).
								</Typography>
							</Box>
							<Box display="flex" justifyContent="center">
								<Button
									variant="contained"
									sx={{ minWidth: 140 }}
									onClick={handleSave}
									disabled={isSaving}
									startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
								>
									{isSaving ? 'Salvando...' : 'Salvar'}
								</Button>
							</Box>
						</Stack>
					</Paper>

				</Stack>
			</Box>
		</Box>
	)
}

export default TabParameters
